import api from '@/api/axios';
import { ENDPOINTS } from '@/api/endpoints';
import {
  Task,
  TaskCreateData,
  TaskUpdateData,
  TaskStatusUpdateData,
  TaskPriorityUpdateData,
  TaskDragDropData,
  TaskFilterParams,
  ClosedTask,
  MasterTask,
  MasterTaskCreateData,
  MasterTaskUpdateData,
} from '@/types';

/**
 * Task API service
 * Replaces Angular's TodoTaskService task-related HTTP methods
 */

// ============ Regular Tasks ============

/**
 * Map backend task object to frontend Task interface
 */
const mapBackendTask = (t: any): Task => ({
  ...t,
  taskTitle: t.taskTitle || t.taskDescription || '',
  assignedTo: t.taskAssignTo || t.assignedTo,
  assignedToName: t.userDetails?.userName,
  createdByName: t.createdUserDetails?.userName,
});

/**
 * Get tasks for a group
 */
export const getGroupTasks = async (groupId: number): Promise<Task[]> => {
  const response = await api.get<{ taskList: any[] }>(ENDPOINTS.TASKS.LIST(groupId));
  const taskList = response.data?.taskList || [];
  return taskList.filter((t: any) => !t.isClosed && t.taskStatus !== 'Closed' && t.taskStatus !== 'Done').map(mapBackendTask);
};

/**
 * Get task details
 */
export const getTaskDetails = async (taskId: number): Promise<Task> => {
  const response = await api.get<any>(ENDPOINTS.TASKS.DETAILS(taskId));
  return mapBackendTask(response.data);
};

/**
 * Create new task
 * Maps frontend field names to backend expected names
 */
export const createTask = async (data: TaskCreateData): Promise<{ status: string; message: string }> => {
  // Map frontend field names to backend expected names
  // Backend stores taskDescription in the 'description' column (the main task content)
  // taskTitle is used as the primary display text
  const backendData = {
    groupId: data.groupId,
    taskTitle: data.taskTitle,
    taskDescription: data.taskTitle,
    taskPriority: data.taskPriority,
    taskDueDate: data.taskDueDate,
    taskAssignTo: data.assignedTo, // Map assignedTo -> taskAssignTo
    // Issue-related fields
    issueTypeId: data.issueTypeId,
    workflowStatusId: data.workflowStatusId,
    sprintId: data.sprintId,
    storyPoints: data.storyPoints,
    parentTaskId: data.parentTaskId,
    startDate: data.startDate,
    endDate: data.endDate,
  };
  const response = await api.post<{ status: string; message: string }>(ENDPOINTS.TASKS.CREATE, backendData);
  return response.data;
};

/**
 * Update task
 */
export const updateTask = async (taskId: number, data: TaskUpdateData): Promise<Task> => {
  // Backend requires taskStatus and groupId — fetch current task to get them
  const current = await getTaskDetails(taskId);
  const backendData = {
    groupId: current.groupId,
    taskDescription: data.taskDescription !== undefined ? data.taskDescription : current.taskDescription || current.taskTitle,
    taskTitle: data.taskTitle || current.taskTitle,
    taskPriority: data.taskPriority || current.taskPriority,
    taskStatus: data.taskStatus || current.taskStatus,
    taskDueDate: data.taskDueDate,
    taskAssignTo: data.assignedTo,
    // PM fields
    workflowStatusId: data.workflowStatusId,
    storyPoints: data.storyPoints,
    sprintId: data.sprintId,
    issueTypeId: data.issueTypeId,
    startDate: data.startDate,
    endDate: data.endDate,
  };
  await api.put<{ status: string; message: string }>(ENDPOINTS.TASKS.UPDATE(taskId), backendData);
  return getTaskDetails(taskId);
};

/**
 * Update task status
 */
export const updateTaskStatus = async (
  taskId: number,
  data: TaskStatusUpdateData
): Promise<Task> => {
  await api.put<{ status: string; message: string }>(ENDPOINTS.TASKS.UPDATE_STATUS(taskId), data);
  return getTaskDetails(taskId);
};

/**
 * Update task priority
 */
export const updateTaskPriority = async (
  taskId: number,
  data: TaskPriorityUpdateData
): Promise<Task> => {
  await api.put<{ status: string; message: string }>(ENDPOINTS.TASKS.UPDATE_PRIORITY(taskId), data);
  return getTaskDetails(taskId);
};

/**
 * Delete task
 */
export const deleteTask = async (taskId: number): Promise<void> => {
  await api.delete(ENDPOINTS.TASKS.DETAILS(taskId));
};

/**
 * Close task
 */
export const closeTask = async (taskId: number): Promise<ClosedTask> => {
  const response = await api.delete<ClosedTask>(ENDPOINTS.TASKS.CLOSE(taskId));
  return response.data;
};

/**
 * Get closed tasks for a group
 */
export const getClosedTasks = async (groupId: number): Promise<ClosedTask[]> => {
  const response = await api.get<{ taskList: any[] }>(ENDPOINTS.TASKS.CLOSED_LIST(groupId));
  const taskList = response.data?.taskList || [];
  return taskList.filter((t: any) => t.isClosed || t.taskStatus === 'Closed' || t.taskStatus === 'Done').map(mapBackendTask) as ClosedTask[];
};

/**
 * Filter tasks
 */
export const filterTasks = async (params: TaskFilterParams): Promise<Task[]> => {
  const response = await api.post<Task[]>(ENDPOINTS.TASKS.FILTER, params);
  return response.data;
};

/**
 * Save drag and drop order
 */
export const saveDragDropOrder = async (data: TaskDragDropData[]): Promise<void> => {
  await api.post(ENDPOINTS.TASKS.DRAG_DROP, data);
};

/**
 * Send task reminder
 */
export const sendTaskReminder = async (taskId: number): Promise<{ message: string }> => {
  const response = await api.post<{ message: string }>(ENDPOINTS.TASKS.REMINDER(taskId), {});
  return response.data;
};

// ============ Master Tasks (Recurring) ============

/**
 * Get master tasks for a group
 */
export const getMasterTasks = async (groupId: number): Promise<MasterTask[]> => {
  const response = await api.get<MasterTask[]>(ENDPOINTS.MASTER_TASKS.LIST(groupId));
  return response.data;
};

/**
 * Get master task details
 */
export const getMasterTaskDetails = async (taskId: number): Promise<MasterTask> => {
  const response = await api.get<MasterTask>(ENDPOINTS.MASTER_TASKS.DETAILS(taskId));
  return response.data;
};

/**
 * Create master task
 */
export const createMasterTask = async (data: MasterTaskCreateData): Promise<MasterTask> => {
  const response = await api.post<MasterTask>(ENDPOINTS.MASTER_TASKS.BASE, data);
  return response.data;
};

/**
 * Update master task
 */
export const updateMasterTask = async (
  taskId: number,
  data: MasterTaskUpdateData
): Promise<MasterTask> => {
  const response = await api.put<MasterTask>(ENDPOINTS.MASTER_TASKS.DETAILS(taskId), data);
  return response.data;
};

/**
 * Delete master task
 */
export const deleteMasterTask = async (taskId: number): Promise<void> => {
  await api.delete(ENDPOINTS.MASTER_TASKS.DETAILS(taskId));
};

export const taskApi = {
  // Regular tasks
  getGroupTasks,
  getTaskDetails,
  createTask,
  updateTask,
  updateTaskStatus,
  updateTaskPriority,
  deleteTask,
  closeTask,
  getClosedTasks,
  filterTasks,
  saveDragDropOrder,
  sendTaskReminder,
  // Master tasks
  getMasterTasks,
  getMasterTaskDetails,
  createMasterTask,
  updateMasterTask,
  deleteMasterTask,
};

export default taskApi;
